/**
 * Витрина: одна вилка на весь экран.
 *
 * Второй режим того же движка. Лента отвечает на вопрос «что сейчас на рынке»,
 * витрина — на «что делать прямо сейчас», и это разные вопросы даже у одного
 * человека в течение часа. Выбор режима запоминается: переключать его каждое
 * утро — это налог, который платит пользователь за нашу нерешительность.
 *
 * Что здесь сделано не так, как «принято в сканерах»:
 *
 * Суммы ног крупные: это то, что вбивают в чужие поля — валюта площадки, не
 * банка. Прибыль в итоге — главный вопрос этой страницы, не подпись к
 * «вернётся» (01.08, Рафик: «сколько я заработаю?» первым, «сколько отдать?»
 * вторым). Поэтому `.gain` правее и крупнее спишется/вернётся, а не мельче них.
 * Итог карточки всегда в долларах; рубли рядом — только если в вилке есть
 * ру-контора (23.08, владелец).
 *
 * Первая нога инвертирована — единственная белая заливка на экране. Она значит
 * ровно одно: делай это первым. Биржа исполняется лимиткой и может не налиться,
 * контора принимает мгновенно; поставив сначала в контору, человек рискует
 * остаться с одной ногой на всю сумму, а это уже не арбитраж, а ставка.
 *
 * Часы показывают возраст вилки против ВАШЕГО измеренного темпа. Пока темпа нет,
 * засечки нет: чужая мерка пугала бы одних и успокаивала других.
 *
 * Вилка без двух названных касс сюда не доезжает: `buildRow` возвращает null.
 * Показывать суммы при пустом слоте площадки нельзя — это уже не арбитраж.
 *
 * Сумма, исход и название матча копируются тем же `Grab`, что карточка и
 * отдельное окно: на слабом ПК выделение мышью не успевает, а в форму конторы
 * вставляют именно эти три поля (08.08, владелец).
 */

import { useCallback, useEffect } from 'react';

import { placeMark } from '../lib/align';
import { type Fork, marketLabel } from '../lib/protocol';
import type { RowLeg } from '../lib/row';
import { tempoOf, useSettings } from '../store/settings';
import { enter, forForm, placed, urlOf } from './act';
import { useEntry } from './entry';
import { sportTitle } from '../lib/sports';
import { useSifted } from './filters';
import { rub, usd } from './format';
import { Grab } from './Grab';
import { InitiatorDot } from './InitiatorDot';
import type { Meta } from './meta';
import { venueName } from './meta';
import { matchTitle, matchWords } from './matchTitle';
import { betOn } from './pick';
import { legsFirstOf } from './order';
import { useProfile } from './profile';
import { legOrder } from './Row';
import type { FeedRow } from './rows';
import {
  cardMoney,
  hasRubBook,
  isRubStake,
  moneyAmount,
  profitText,
  useShow,
} from './show';
import { Sign } from './Sign';
import { stillness, stillText } from './still';
import { blankStep, useScreen } from './store';
import { logoOf } from './venues';

import './showcase.css';

/** Сколько строк очереди живёт за сгибом. Больше — это уже лента, а не «дальше». */
const QUEUE = 4;

const title = (row: FeedRow): string => matchTitle(row);

/** В поиск конторы — без тире: так же копирует раскрытая карточка. */
const matchOf = (row: FeedRow): string => matchWords(row);

/** Крупное число ноги: центы у биржи, валюта кассы у конторы. */
function amountOf(leg: RowLeg, fx: number, meta: Meta): { big: string; unit: string } {
  if (leg.unit === 'share') return { big: String(leg.cents ?? 0), unit: '¢' };
  const shown = moneyAmount(leg, fx, meta);
  const unit = shown.endsWith('$') ? '$' : '₽';
  return { big: shown.slice(0, -unit.length).trim(), unit };
}

/** Нога на полосе раскладки: сколько спишет ЭТА площадка. */
function stakeSay(leg: RowLeg, fx: number, meta: Meta): string {
  if (leg.unit === 'share') return `${usd(leg.usd ?? 0)} $`;
  if (isRubStake(leg, meta)) return `${rub(leg.rub)} ₽`;
  return `${usd(leg.usd ?? stakeUsdFallback(leg, fx))} $`;
}

function stakeUsdFallback(leg: RowLeg, fx: number): number {
  return fx > 0 ? (leg.lay ? (leg.layStake ?? leg.rub) : leg.rub) / fx : 0;
}

function Leg({
  leg,
  index,
  meta,
  rowId,
  fork,
}: {
  leg: RowLeg;
  index: number;
  meta: Meta;
  rowId: string;
  fork: Fork;
}): React.ReactElement {
  const share = leg.unit === 'share';
  const fx = useSettings((s) => s.fx);
  const amount = amountOf(leg, fx, meta);
  const form = forForm(leg);
  const at = venueName(meta, leg.source);
  const logo = logoOf(leg.source);
  const href = urlOf(rowId, leg);
  const side = betOn(leg, fork);
  return (
    <li className={`nx-leg ${index === 0 ? 'nx-leg--go' : 'nx-leg--then'}`}>
      <span className="nx-leg__n">{index + 1}</span>
      {/* Название конторы и есть вход: отдельная кнопка «открыть» отняла бы
          колонку у чисел, ради которых экран существует. Ноги без входа
          говорят об этом прямо — молчание человек читает как «вход есть» и
          узнаёт правду, уже держа первую ногу.
          Имя — сосед ссылки, не потомок. Два выката красили `<b>` внутри
          `<a>`: UA :visited, глобальный `a { color }` и `min-width: 0` на
          flex-ссылке снова съедали текст. Знак при этом оставался — он уже
          был снаружи. Цифры тоже. */}
      <span className="nx-leg__at">
        <InitiatorDot
          initiator={leg.initiator}
          initiatorCount={leg.initiatorCount}
          by={typeof fork.initiatorBy === 'string' ? fork.initiatorBy : undefined}
        />
        {logo !== null && (
          <img className="nx-leg__mk" src={logo} alt="" loading="lazy" decoding="async" />
        )}
        <b className="nx-leg__book">{at}</b>
        {href === null ? (
          <i className="nx-leg__no">ссылки нет, искать руками</i>
        ) : (
          <a
            className="nx-leg__open"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={at}
          />
        )}
      </span>
      <span className="nx-leg__sum">
        <Grab text={form} what="сумму">
          {amount.big}
        </Grab>
        <i>{amount.unit}</i>
      </span>
      <span className="nx-leg__pick">
        <Grab text={side} what="исход">
          {share ? `${side} · ${(leg.shares ?? 0).toFixed(1)} шер` : side}
        </Grab>
      </span>
      <span className="nx-leg__px">
        {share ? `${(leg.usd ?? 0).toFixed(2)} $` : `кэф ${leg.odds}`}
        {leg.ownOdds && !share && leg.oddsLive !== undefined && leg.oddsLive !== leg.odds
          ? ` · лента ${leg.oddsLive}`
          : null}
      </span>
    </li>
  );
}

export function Showcase(): React.ReactElement {
  const { big, tail } = useSifted();
  const meta = useScreen((s) => s.meta);
  const status = useScreen((s) => s.status);
  const framed = useScreen((s) => s.framed);
  const skipped = useScreen((s) => s.skipped);
  const skip = useScreen((s) => s.skip);
  const pinned = useScreen((s) => s.pinned);
  const pick = useScreen((s) => s.pick);
  const steps = useScreen((s) => s.steps);
  const attempts = useSettings((s) => s.attempts);
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);
  const legsFirst = legsFirstOf(useProfile((s) => s.profile?.legsFirst));
  const show = useShow((s) => s.unit);
  const warm = useEntry((s) => s.warm);
  const pair = useSettings((s) => s.pairOpen);
  const tempo = tempoOf(attempts);

  // Поднятая руками идёт первой, остальное — по деньгам. Порядок считается
  // здесь, а не хранится: список приходит заново каждым снапшотом, и
  // сохранённый порядок означал бы показ вилки, которой уже нет.
  const buried = useScreen((s) => s.hiddenForks);
  const live = [...big, ...tail].filter(
    (row) => !skipped.includes(row.id) && row.ghost === null && !buried.has(row.id),
  );
  const queue = [...live].sort(
    (a, b) => Number(b.id === pinned) - Number(a.id === pinned),
  );
  const row = queue[0];

  useEffect(() => {
    return () => {
      const lock = useScreen.getState().stakeLock;
      if (lock !== null && (row === undefined || lock.forkId === row.id)) {
        useScreen.getState().setStakeLock(null);
      }
      const odds = useScreen.getState().oddsLock;
      if (odds !== null && (row === undefined || odds.forkId === row.id)) {
        useScreen.getState().setOddsLock(null);
      }
      const fees = useScreen.getState().feeLock;
      if (fees !== null && (row === undefined || fees.forkId === row.id)) {
        useScreen.getState().setFeeLock(null);
      }
      const curs = useScreen.getState().curLock;
      if (curs !== null && (row === undefined || curs.forkId === row.id)) {
        useScreen.getState().setCurLock(null);
      }
    };
  }, [row?.id]);

  /**
   * Одно нажатие — один шаг, и шагов на ногу два: открыть контору и сказать
   * «поставил». Раньше это было одно действие, и вкладка, открытая из
   * любопытства, записывалась в измеренный темп (08.08, владелец). Пробел
   * по-прежнему ведёт всю вилку — просто нажатий четыре, а не два.
   */
  const take = useCallback((): void => {
    if (row === undefined) return;
    const step = steps[row.id] ?? blankStep();
    const index: 1 | 2 = step.first === null ? 1 : 2;

    // Первый шаг ноги: вкладка конторы и сумма в буфере. Ставки ещё нет,
    // счётчики молчат. Само действие — общее с карточкой и клавиатурой
    // (`act.ts`): расходиться трём путям входа нельзя.
    if (step.opened[index - 1] === null) {
      enter(row, index, pair);
      return;
    }
    placed(row, index);
  }, [row, steps, pair]);

  // Греем вход, пока человек читает строку: к моменту нажатия адрес события и
  // сумма уже известны, и вкладка открывается без единого ожидания сети.
  // Отпечаток в зависимостях: уехал кэф — купон спрашивают заново, не через 20 с.
  const ticket = row === undefined ? '' : placeMark(row.row.fork, bank, fx);
  useEffect(() => {
    if (row === undefined) return;
    warm(row.id, bank, fx, ticket);
  }, [row?.id, bank, fx, ticket, warm]);

  // Пробел — взять, стрелка вправо — дальше. Обе руки на клавиатуре: витрина
  // рассчитана на то, что человек смотрит на числа, а не ищет курсором кнопку.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target !== null && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (event.code === 'Space') {
        event.preventDefault();
        take();
      }
      if (event.key === 'ArrowRight' && row !== undefined) skip(row.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [take, skip, row]);

  if (row === undefined) {
    const ready = framed && status !== 'reconnecting' && status !== 'paused' && status !== 'revoked';
    const title = ready ? 'брать сейчас нечего'
      : status === 'paused' ? 'лента обновляется'
        : status === 'reconnecting' ? 'восстанавливаем связь' : 'ждём данные ленты';
    const reason = ready ? 'в полученной ленте сейчас нет подходящей вилки для ваших настроек.'
      : !framed ? 'данные ещё не получены. количество вилок пока неизвестно.'
        : 'свежие данные временно недоступны. ждём восстановления обновлений.';
    return (
      <main className="nx-stage">
        <div className="nx-hold nx-card">
          <p className="nx-empty__t">{title}</p>
          <p className="nx-empty__p">{reason}</p>
        </div>
      </main>
    );
  }

  const legs = legOrder(row.row.legs, legsFirst);
  const step = steps[row.id] ?? blankStep();
  const first = legs[0]!;
  const share = first.rub;
  const sharePct = row.row.staked > 0 ? Math.round((share / row.row.staked) * 100) : 50;
  const life = row.lifespan ?? 0;
  const lived = life > 0 ? Math.min(100, Math.round((row.age / life) * 100)) : 100;
  const mark = life > 0 && tempo !== null ? Math.min(100, (tempo / life) * 100) : null;
  const stuck = stillness(row.row.fork);
  const withRub = hasRubBook(legs, meta);
  const gain = profitText(row.row.profit, row.row.roiPct, fx, show);
  const staked = cardMoney(row.row.staked, fx, withRub);
  const payout = cardMoney(row.row.payout, fx, withRub);
  const extra = (one: { extra: string | null }): string =>
    one.extra === null ? '' : ` · ${one.extra}`;

  return (
    <>
      <main className="nx-stage">
        <div
          className="nx-hold nx-card"
          data-fork-row=""
          data-id={row.id}
          onMouseEnter={() => warm(row.id, bank, fx, ticket)}
        >
          <div className="nx-hold__who">
            <Sign sport={row.row.fork.sport} name={row.row.fork.sportName} />
            <span>{sportTitle(row.row.fork)}</span>
            <i>/</i>
            <Grab text={matchOf(row)} what="название матча" as="b">
              {title(row)}
            </Grab>
            <i>/</i>
            <span>{marketLabel(row.row.fork.market, row.row.fork.sport)}</span>
          </div>

          {/* Строки тревоги в норме нет вовсе: молчание и означает, что всё
              сошлось. Постоянная плашка «проверено» перестаёт читаться на
              третий день и обесценивает настоящее предупреждение.
              Непроверенное (`checks.unknown`) сюда больше не поднимает плашку: на
              боевой ленте это всегда `spread` и `motion` — стакана у букмекера
              нет по природе площадки, — то есть плашка стояла над каждой вилкой
              витрины и была именно тем фоном, против которого написан абзац
              выше (11.08, владелец). */}
          {(row.risky || stuck !== null) && (
            <div className="nx-warn">
              <u>{stuck !== null ? 'кэф давно не меняется' : 'вилка может не дожить'}</u>
              <span>
                {/* Залипшая цена идёт первой из тревог: она отвечает не «успеете
                    ли», а «есть ли вообще что брать». Замер 06.08 по донору —
                    58.9 % ног не двигались дольше 150 с, и верх списка по
                    проценту это они. */}
                {stuck !== null
                  ? `в ${venueName(meta, stuck.source)} кэф стоит ${stillText(stuck)}. откройте контору и сверьте`
                  : `такие живут ${life} с, а вы тратите ${tempo} с. вторую ставку можно не успеть`}
              </span>
            </div>
          )}

          <ol className="nx-legs">
            {legs.map((leg, i) => (
              <Leg
                key={`${leg.source}:${i}`}
                leg={leg}
                index={i}
                meta={meta}
                rowId={row.id}
                fork={row.row.fork}
              />
            ))}
          </ol>

          {/* Полоса раскладки: две валюты на одном отрезке. Человек видит, что
              банк не «уходит на ставку», а делится между двумя площадками. */}
          <div className="nx-split">
            <div className="nx-split__bar" style={{ ['--w' as string]: sharePct }}>
              <i />
              <i />
            </div>
            <div className="nx-split__legend">
              <span>
                {venueName(meta, first.source)} <b>{stakeSay(first, fx, meta)}</b>
              </span>
              <span>
                {venueName(meta, legs[1]!.source)} <b>{stakeSay(legs[1]!, fx, meta)}</b>
              </span>
            </div>
          </div>

          <div className="nx-tot">
            <span>
              спишется <b>{staked.main}</b>
              {extra(staked)}
            </span>
            <span className="win">
              вернётся <b>{payout.main}</b>
              {extra(payout)}
            </span>
            <span className="gain">{gain}</span>
          </div>

          <div className="nx-act">
            <button className="nx-take" type="button" onClick={take}>
              {step.second !== null
                ? `обе стоят, ${gain}`
                : step.first !== null
                  ? step.opened[1] === null
                    ? `вторая: ${venueName(meta, legs[1]!.source)}`
                    : 'поставил вторую'
                  : step.opened[0] === null
                    ? `взять ${venueName(meta, first.source)}`
                    : 'поставил первую'}{' '}
              <kbd>пробел</kbd>
            </button>
            <div className="nx-clock">
              <div className="nx-clock__track">
                <b style={{ ['--w' as string]: lived }} />
                {mark !== null && <i style={{ ['--x' as string]: mark }} />}
              </div>
              {/* Цифры на полосе, не абзацем под ней: три величины через точку
                  читались дольше, чем живёт вилка. Тип «такие живут» уехал
                  в ленту дробью возраст/жизнь. «Вы тратите» — засечка на
                  полосе, без второй подписи. Пока темпа нет — одна фраза,
                  иначе экран врёт чужой меркой (08.08). */}
              <div className="nx-clock__nums">
                <span className="nx-clock__now">{row.age} с</span>
                {life > 0 && <span className="nx-clock__typ">{life} с</span>}
              </div>
              {tempo === null && (
                <p className="nx-clock__say">ваше время замерим после первого взятия</p>
              )}
            </div>
            <button className="nx-skip" type="button" onClick={() => skip(row.id)}>
              дальше <kbd>→</kbd>
            </button>
          </div>

          <p className="nx-more">
            {queue.length > 1 ? `ниже ещё ${queue.length - 1}` : 'других сейчас нет'}
          </p>
        </div>
      </main>

      <section className="nx-queue">
        <div className="nx-hold">
          <h2>дальше</h2>
          {queue.slice(1, 1 + QUEUE).map((next) => (
            <button className="nx-q" type="button" key={next.id} onClick={() => pick(next.id)}>
              <span className="nx-q__g">
                {profitText(next.row.profit, next.row.roiPct, fx, show)}
              </span>
              <span className="nx-q__m">{title(next)}</span>
              <span className="nx-q__b">
                {legOrder(next.row.legs, legsFirst)
                  .map((leg) => venueName(meta, leg.source))
                  .join(' · ')}
              </span>
              <span className="nx-q__t">{next.age} с</span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
